/**
 * `addressOf` — the one-authority for a row's **data name** (slice L0 of the
 * row-identity ledger design, §1.3). A row has three names — *placement* (`serial`), *wire* (`rawId`),
 * and *data* (this address). `addressOf` computes the data name as the closure
 * of the existing `DataRef` resolution rules over **every** row provenance.
 *
 * The data name is what dependencies should key on: promote (wire re-key) and
 * alias re-expansion (id re-mint) keep it fixed, so state hung off it survives
 * the churn that breaks wire-keyed and instance-keyed state today. A *genuine*
 * data change (weather relocation re-keys the descriptor) changes it — that
 * change is the invalidation signal.
 *
 * **Contract (pinned by the repository's fuzz/property suite):**
 *  - **Total** — every hydratable row classifies (`resolve` never throws).
 *  - **Injective** — no two distinct data streams share an address (LI5).
 *  - **Stable** — promote / re-expansion / same-data reparent never change an
 *    address (LI6); only a genuine data-identity change (relocation) does.
 *
 * Addresses are **opaque labels**: consumers never parse them. The two
 * serialized forms are `data:<lookupKey>` and `data:local:<serial>`. The
 * `serial`-bearing form is **in-memory only** — it MUST NOT leak into any
 * persisted key (OQ1; asserted by the L0 suite).
 *
 * This module has **no call sites yet** — L0 pins the contract before anything
 * is wired to it (no behavior change).
 */

import type { Option } from "../core/option.ts";
import { resolveRelativeLookupKey } from "./data-ref-resolve.ts";
import type { LookupKeyNode } from "./data-ref-resolve.ts";
import type { RowKey } from "./row-key.ts";
import type { RowLocalId } from "./row-local-id.ts";

/**
 * A row's data name. Branded (`string & { unique symbol }`) so the projection
 * is non-interchangeable at compile time with the wire (`rawId`) and placement
 * (`serial`) projections — brands assign *to* `string`, never *from* it, at
 * zero runtime cost. Mirrors the sheet-level `SheetKey.MapKey` brand
 * (`src/client/sheet-key.ts:45`).
 */
export type Address = string & { readonly __addressBrand: unique symbol };

export namespace AddressOf {
  /**
   * Why a row has **no** data node. Placement-only rows are structure, not
   * data — the ledger hangs no dependency edges on them.
   *
   *  - `pending`   — an optimistic `pending(uuid)` row awaiting a server id
   *                  (its data node appears once it promotes to a real row).
   *  - `template`  — a `template(name)` definition/scaffolding row (catalog
   *                  builtins) — a shape, not a live stream.
   *  - `system`    — a `system(...)` layout/structural row (headers, all-day
   *                  and timeline hosts) — it *positions* data owned elsewhere.
   *  - `container` — an alias row (`AliasRow`) carrying no concrete lookup
   *                  form: a template container whose data lives in the
   *                  descendants it expands (the `list` aggregate + per-item
   *                  sources), each of which gets its own address. This is the
   *                  pinned rule for **calendar-list**, which deliberately
   *                  declares no `dataRef` (`preferences/calendar-aliases.ts`).
   */
  export type PlacementReason = "pending" | "template" | "system" | "container";

  export type Result =
    | { readonly kind: "data"; readonly address: Address }
    | { readonly kind: "placement-only"; readonly reason: PlacementReason };

  /**
   * The minimal structural row surface `resolve` classifies over — a superset
   * of `LookupKeyNode` (`data-ref-resolve.ts`) so the existing relative-address
   * resolver applies unchanged. Kept structural (no dependency on the concrete
   * row classes) so the classifier is unit-testable with hand-built mocks;
   * `RowAddress.forRow` (`rows/row-address.ts`) adapts a live `ModelRow`.
   */
  export interface AddressableRow extends LookupKeyNode {
    /** Placement identity — immutable, survives promote (`RowLocalId.serial`). */
    readonly serial: string;
    /** Wire key — variant-tagged (`user`/`lookup`/`reserved`/…). */
    readonly key: RowKey;
    /**
     * The row's resolved concrete lookup key (stored, or the `ComputedDataSource`
     * getter's relative resolution), or `undefined`. Narrows `LookupKeyNode`.
     */
    readonly lookupKey: Option<string>;
    /** A declared relative address (`ComputedDataSource.localPath`), or unset. */
    readonly localPath: Option<string>;
    /** Parent in the tree, or unset at root. Narrows `LookupKeyNode.parent`. */
    readonly parent: Option<AddressableRow>;
    /**
     * The source row this row projects (renderer rows only), or `undefined`.
     * A renderer carries no address of its own — it resolves to the source's.
     */
    readonly rendererSource: Option<AddressableRow>;
    /**
     * True for template-container rows (`AliasRow`) whose data lives in the
     * descendants they expand — see `PlacementReason.container`.
     */
    readonly isContainer: boolean;
  }

  /**
   * Classify a row's data name — **total**, never throws.
   *
   * Precedence (design §1.3):
   *  1. Renderer rows carry no address of their own → the source's address.
   *  2. Any row that resolves a concrete lookup/data key → `data:<key>`. This
   *     covers reserved/canonical (`:~`) owners, plain `:` lookup rows,
   *     non-UUID serde ids (which intern as lookup-form keys — `row-id.ts:419`,
   *     so `data:<that string>`), user/virtual rows carrying a lookupKey (the
   *     deliberate divergence from `cellAccessKey`, which keys such rows by
   *     row-id string), alias rows declaring a lookup form (e.g. weather's
   *     `:~lona:weather`), and computed rows whose relative `localPath`
   *     resolves against a keyed ancestor.
   *  3. `pending` / `template` / `system` variants and alias containers →
   *     placement-only (no data node).
   *  4. Otherwise a placement-backed local stream (uuid/virtual row with no
   *     lookup form, or a computed row whose relative ref does not yet resolve)
   *     → `data:local:<serial>`. NOT the wire key: `serial` survives
   *     promote; an unresolved relative ref re-resolves (→ step 2) on reparent.
   */
  export function resolve(row: AddressableRow): Result {
    // 1. Renderers delegate to the source they paint.
    const source = row.rendererSource;
    if (source != null) return resolve(source);

    // 2. A concrete lookup/data key is a global data name.
    const key = concreteLookupKey(row);
    if (key != null) return { kind: "data", address: makeAddress(`data:${key}`) };

    // 3. Placement-only classifications (no data node).
    if (row.key.isPending()) return { kind: "placement-only", reason: "pending" };
    if (row.key.isTemplate()) return { kind: "placement-only", reason: "template" };
    if (row.key.isSystem()) return { kind: "placement-only", reason: "system" };
    if (row.isContainer) return { kind: "placement-only", reason: "container" };

    // 4. Placement-keyed local stream.
    return { kind: "data", address: makeAddress(`data:local:${row.serial}`) };
  }

  /** Thin wrapper: the address, or `null` for placement-only rows. */
  export function addressOf(row: AddressableRow): Option<Address> {
    const result = resolve(row);
    return result.kind === "data" ? result.address : null;
  }

  /**
   * The address a `RowLocalId` affords **on its own** — before the live
   * `ModelRow` is available (the ledger's pre-attachment fallback, L2). A
   * lookup/reserved-variant wire key IS a data name → `data:<wireKey>` (matches
   * `resolve` for such rows, where `lookupKey === rawId`); anything else falls
   * back to the placement-keyed local stream `data:local:<serial>`. Coarser
   * than `resolve` (which, given the live row, can see pending/template/system/
   * container and return placement-only) — this is used only until
   * `attachRowLookup` delegates to the live-row `resolve`, and its output is
   * always a data node so a bump always has a target.
   */
  /**
   * The data address of a canonical lookup key (`:~weather:…`): the node a
   * canonical store's cells share with every sheet row that resolves to it.
   */
  export function forLookupKey(key: string): Address {
    return makeAddress(`data:${key}`);
  }

  export function forLocalId(id: RowLocalId): Address {
    const key = id.key;
    if (key.isLookup() || key.isReserved()) return makeAddress(`data:${id.rawId}`);
    return makeAddress(`data:local:${id.serial}`);
  }

  // ── Internal ────────────────────────────────────────────────────────────

  /**
   * The row's concrete lookup/data key, or `null` when it owns no lookup form.
   *
   * Lookup/reserved-variant rows (including non-UUID serde ids, which intern as
   * lookup-form keys) name their data by the wire key itself. Other rows use a
   * stored/resolved `lookupKey`, then a relative `localPath` resolved against
   * the nearest keyed ancestor (`resolveRelativeLookupKey` — the same primitive
   * `ComputedDataSource` uses).
   */
  function concreteLookupKey(row: AddressableRow): Option<string> {
    const key = row.key;
    if (key.isLookup() || key.isReserved()) return row.lookupKey ?? key.toString();
    if (row.lookupKey != null) return row.lookupKey;
    if (row.localPath != null) return resolveRelativeLookupKey(row, row.localPath) ?? null;
    return null;
  }

  /** The single brand-cast site (the accepted branded-string pattern). */
  function makeAddress(raw: string): Address {
    return raw as Address;
  }
}
